/*
  Installed from https://reactbits.dev/ts/tailwind/
  Modified: constant speed, single direction, no scroll coupling
*/

import React, { useRef, useLayoutEffect, useState } from "react";
import {
  motion,
  useMotionValue,
  useTransform,
  useAnimationFrame,
} from "motion/react";

interface VelocityMapping {
  input: [number, number];
  output: [number, number];
}

interface VelocityTextProps {
  children: React.ReactNode;
  baseVelocity: number; // px/second, sign decides direction
  scrollContainerRef?: React.RefObject<HTMLElement>;
  className?: string;
  damping?: number; // kept for API compat (unused)
  stiffness?: number; // kept for API compat (unused)
  numCopies?: number;
  velocityMapping?: VelocityMapping; // kept for API compat (unused)
  parallaxClassName?: string;
  scrollerClassName?: string;
  parallaxStyle?: React.CSSProperties;
  scrollerStyle?: React.CSSProperties;
}

interface ScrollVelocityProps {
  scrollContainerRef?: React.RefObject<HTMLElement>;
  texts: string[];
  velocity?: number; // px/second (positive => right, negative => left)
  className?: string;
  damping?: number; // kept for API compat (unused)
  stiffness?: number; // kept for API compat (unused)
  numCopies?: number;
  velocityMapping?: VelocityMapping; // kept for API compat (unused)
  parallaxClassName?: string;
  scrollerClassName?: string;
  parallaxStyle?: React.CSSProperties;
  scrollerStyle?: React.CSSProperties;
}

function useElementWidth<T extends HTMLElement>(ref: React.RefObject<T | null>): number {
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    function updateWidth() {
      if (ref.current) setWidth(ref.current.offsetWidth);
    }
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [ref]);

  return width;
}

export const ScrollVelocity: React.FC<ScrollVelocityProps> = ({
  scrollContainerRef, // kept for API compat (unused)
  texts = [],
  velocity = 100,
  className = "",
  damping = 50, // unused
  stiffness = 400, // unused
  numCopies = 6,
  velocityMapping = { input: [0, 1000], output: [0, 5] }, // unused
  parallaxClassName,
  scrollerClassName,
  parallaxStyle,
  scrollerStyle,
}) => {
  function VelocityText({
    children,
    baseVelocity = velocity,
    scrollContainerRef: _scrollContainerRef,
    className = "",
    damping: _damping,
    stiffness: _stiffness,
    numCopies,
    velocityMapping: _velocityMapping,
    parallaxClassName,
    scrollerClassName,
    parallaxStyle,
    scrollerStyle,
  }: VelocityTextProps) {
    const baseX = useMotionValue(0);

    const copyRef = useRef<HTMLSpanElement>(null);
    const copyWidth = useElementWidth(copyRef);

    function wrap(min: number, max: number, v: number): number {
      const range = max - min;
      const mod = (((v - min) % range) + range) % range;
      return mod + min;
    }

    const x = useTransform(baseX, (v) => {
      if (copyWidth === 0) return "0px";
      return `${wrap(-copyWidth, 0, v)}px`;
    });

    // Constant speed, single direction (sign of baseVelocity), no scroll coupling
    const dir = baseVelocity >= 0 ? 1 : -1;
    useAnimationFrame((_, delta) => {
      const pxPerSec = Math.abs(baseVelocity);
      const moveBy = dir * pxPerSec * (delta / 1000);
      baseX.set(baseX.get() + moveBy);
    });

    const spans = [] as React.ReactNode[];
    for (let i = 0; i < (numCopies ?? 6); i++) {
      spans.push(
        <span
          className={`flex-shrink-0 ${className}`}
          key={i}
          ref={i === 0 ? copyRef : null}
        >
          {children}
        </span>
      );
    }

    return (
      <div className={`${parallaxClassName ?? ""} relative overflow-hidden`} style={parallaxStyle}>
        <motion.div
          className={`${scrollerClassName ?? ""} flex whitespace-nowrap text-center font-sans text-4xl font-bold tracking-[-0.02em] drop-shadow md:text-[5rem] md:leading-[5rem]`}
          style={{ x, ...(scrollerStyle || {}) }}
        >
          {spans}
        </motion.div>
      </div>
    );
  }

  return (
    <section>
      {texts.map((text: string, index: number) => (
        <VelocityText
          key={index}
          className={className}
          baseVelocity={velocity} // same direction for all; use negative to reverse
          scrollContainerRef={scrollContainerRef}
          damping={damping}
          stiffness={stiffness}
          numCopies={numCopies}
          velocityMapping={velocityMapping}
          parallaxClassName={parallaxClassName}
          scrollerClassName={scrollerClassName}
          parallaxStyle={parallaxStyle}
          scrollerStyle={scrollerStyle}
        >
          {text}
        </VelocityText>
      ))}
    </section>
  );
};

export default ScrollVelocity;
